import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { selectNav } from '../../rootSelectors';
import { pathForState } from '../../../router/definitions';

/**
 * Plugin-page navigation, driven from redux.
 *
 * 104 React files dispatch `navigateToPluginPage`, `navigateToChildPage` or
 * `navigateToParentPage` with a plugin name, a page name and a positional argument list. That
 * writes `{pluginName, pageName, args, key}` into the store, and this watches `key` and moves.
 *
 * SURF-4809 changed only the last step. It used to call a `navigateToState` prop, which was
 * Angular's `$state.go` handed down through `react2angular`; now it is the router's own
 * `navigate`. The action shape, the payload and all 104 call sites are untouched, which is
 * deliberate: it keeps the largest surface in the router swap at zero.
 *
 * `key` is a fresh uuid per dispatch, so navigating to the page you are already on still
 * fires. Several screens rely on that to re-enter a page with different arguments.
 */
export default function useNavigation() {
  const { pluginName, pageName, args, key } = selectNav();
  const navigate = useNavigate();

  const go = useCallback(() => {
    if (!pluginName) return;
    const to = pathForState('main.plugin', { pluginName, pageName, params: args });
    if (to) navigate({ to });
  }, [navigate, pluginName, pageName, args]);

  // Keyed on `key` alone, as the Angular version was: it is the signal that a navigation was
  // requested. Including `go` would re-fire on every render that changed any of its inputs,
  // which would fight the router. `goRef` keeps the effect honest about what it calls without
  // widening the dependency list.
  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    goRef.current();
  }, [key]);
}
